import React from 'react';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { STATUS_CHIPS, SOURCE_CHIPS } from './chipConfig';

// Generic chip row used by both filter dimensions. Disabled chips (count=0
// in the cross-aware count map) get a dim style and stop accepting clicks
// — saves admins from dead-end combinations.
function ChipRow({
  chips, activeValue, onChange, counts,
  buttonClass, iconClass, badgeClass, testIdPrefix,
}) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testIdPrefix}-chips`}>
      {chips.map((chip) => {
        const Icon = chip.icon;
        const isActive = activeValue === chip.value;
        const count = counts[chip.value] || 0;
        const isDisabled = !isActive && count === 0;
        return (
          <button
            key={chip.value}
            type="button"
            disabled={isDisabled}
            onClick={() => !isDisabled && onChange(chip.value)}
            data-testid={`${testIdPrefix}-chip-${chip.value.toLowerCase()}`}
            data-disabled={isDisabled || undefined}
            title={isDisabled ? 'No bookings match this combination' : undefined}
            className={`inline-flex items-center gap-2 rounded-full border font-semibold transition-colors ${buttonClass} ${
              isActive
                ? chip.activeBg
                : isDisabled
                  ? 'bg-slate-50 text-slate-400 border-slate-200 cursor-not-allowed opacity-60'
                  : chip.inactiveBg
            }`}
          >
            <Icon className={iconClass} />
            <span>{chip.label}</span>
            <span
              className={`min-w-[20px] rounded-full text-center font-bold ${badgeClass} ${
                isActive
                  ? chip.countBadgeOnActive
                  : isDisabled
                    ? 'bg-slate-200 text-slate-400'
                    : chip.countBadge
              }`}
            >
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function FacetFilters({
  statusFilter, setStatusFilter, statusCounts,
  sourceFilter, setSourceFilter, sourceCounts,
}) {
  return (
    <Card className="p-4 mb-6" data-testid="facet-filters">
      <div className="mb-3">
        <Label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2 block">
          Filter by status
        </Label>
        <ChipRow
          chips={STATUS_CHIPS}
          activeValue={statusFilter}
          onChange={setStatusFilter}
          counts={statusCounts}
          buttonClass="px-4 py-2 text-sm"
          iconClass="w-4 h-4"
          badgeClass="px-2 py-0.5 text-[11px]"
          testIdPrefix="status"
        />
      </div>

      <div className="border-t border-slate-100 my-3" />

      <div>
        <Label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground mb-2 block">
          Filter by source
        </Label>
        <ChipRow
          chips={SOURCE_CHIPS}
          activeValue={sourceFilter}
          onChange={setSourceFilter}
          counts={sourceCounts}
          buttonClass="px-3 py-1.5 text-xs"
          iconClass="w-3.5 h-3.5"
          badgeClass="px-1.5 py-0.5 text-[10px]"
          testIdPrefix="source"
        />
      </div>
    </Card>
  );
}
